import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Bot, QrCode, TrendingUp, Star, Users, Zap, Check, Sparkles, Gift, Package, Mail, Search, ShieldCheck, Share2, ArrowRight, User, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import Logo from '@/components/Logo';
import '../styles/pricing-animations.css';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPageView, trackInitiateCheckout } from '../services/facebookTracking';

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const [isMuted, setIsMuted] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const videoRef = useRef(null);

  // Track PageView on mount
  useEffect(() => {
    trackPageView();
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  // Fonction pour démarrer le checkout Stripe
  const handleStartCheckout = async () => {
    setIsCheckoutLoading(true);
    
    // Track InitiateCheckout event
    trackInitiateCheckout(89.0, 'EUR');
    
    try {
      const response = await axios.post(`${API}/api/payments/create-checkout`, {
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Ouvrir Stripe dans un nouvel onglet
        window.open(response.data.checkout_url, '_blank');
      } else {
        toast.error('Erreur lors de la création de la session de paiement');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erreur lors de la redirection vers le paiement');
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const features = [
    {
      image: 'https://customer-assets.emergentagent.com/job_fortune-wheel-fix/artifacts/h9zrdlme_A-%20reponses%20ia%20aux%20avis.png',
      title: 'Réponses IA aux Avis',
      description: 'Générez automatiquement des réponses personnalisées aux avis clients grâce à l\'IA'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_fortune-wheel-fix/artifacts/ds2p3h1w_A-%20seo%20en%20continue%20avec%20l%27ia.png',
      title: 'SEO en Continu avec l\'IA',
      description: 'Optimisation automatique pour apparaître en premier sur Google quand les clients cherchent votre restaurant ou des mots-clés similaires'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_fortune-wheel-fix/artifacts/bxkon8ib_A-%20jeu%20de%20la%20roue.png',
      title: 'Jeu de la Roue',
      description: 'Fidélisez vos clients avec un jeu de roue de la fortune et récupérez automatiquement leurs données de contact'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_fortune-wheel-fix/artifacts/itrfiekp_A-%20promo%20en%20masse%20sms%20%26%20email.png',
      title: 'Promos en Masse par SMS & Email',
      description: 'Envoyez des promotions à tous vos clients existants en un clic. 100x plus efficace que les flyers traditionnels'
    }
  ];

  const offerFeatures = {
    included: [
      {
        icon: <Gift className="w-7 h-7" />,
        title: 'Jeu de la roue cadeaux interactif',
        description: 'Captivez vos clients avec une expérience ludique qui stimule la collecte d\'avis et renforce la fidélisation'
      },
      {
        icon: <Package className="w-7 h-7" />,
        title: 'Livraison automatique des cadeaux',
        description: 'Multipliez vos avis Google et améliorez votre note sans effort, le tout en automatique'
      },
      {
        icon: <Mail className="w-7 h-7" />,
        title: 'Plateforme Marketing Intelligente',
        description: 'Remplacez vos flyers papier par des campagnes SMS ciblées à fort ROI, touchez directement vos clients et boostez vos ventes sans gaspillage'
      },
      {
        icon: <Search className="w-7 h-7" />,
        title: 'Référencement SEO automatisé par IA',
        description: 'Des optimisations SEO hebdomadaires pour propulser votre visibilité sur Google sans la moindre intervention de votre part'
      },
      {
        icon: <ShieldCheck className="w-7 h-7" />,
        title: 'Vérifications avancées par SMS',
        description: 'Authentification renforcée par numéro de téléphone, augmentant la fiabilité et la valeur de votre base de données clients'
      },
      {
        icon: <Bot className="w-7 h-7" />,
        title: 'Réponses IA personnalisées aux avis clients',
        description: 'Notre IA gère intelligemment vos réponses aux avis Google, construisant en continu une image professionnelle et attentionnée'
      },
      {
        icon: <Share2 className="w-7 h-7" />,
        title: 'Publications multi-réseaux en 1 clic',
        description: 'Publiez et programmez simultanément sur Facebook, Instagram et TikTok en quelques secondes seulement'
      }
    ],
    bonuses: [
      { text: 'Mise en place complète offerte', value: '199€' },
      { text: 'Design custom de vos supports marketing offert', value: 'Inclus' },
      { text: 'Support prioritaire multi-canal (WhatsApp, chat et téléphone)', value: 'Inclus' },
      { text: 'Tarifs préférentiels pour vos campagnes SMS marketing', value: 'Inclus' }
    ]
  };

  const testimonials = [
    {
      name: 'Said E.',
      business: 'Restaurant Le Majestic',
      text: 'Markeap a transformé notre façon d\'engager nos clients. Nous avons constitué une base de 2 000+ clients fidèles en seulement 3 mois !',
      avatar: 'https://customer-assets.emergentagent.com/job_saas-enhance/artifacts/9xwf8c63_Gemini_Generated_Image_9ao8qo9ao8qo9ao8__1_-removebg-preview.png'
    },
    {
      name: 'Bala C.',
      business: 'Barber Avenue Paris',
      text: 'Les réponses IA aux avis me font gagner des heures chaque semaine. Professionnelles, personnelles, et nos notes en ligne se sont améliorées !',
      avatar: 'https://customer-assets.emergentagent.com/job_saas-enhance/artifacts/qafm6rj2_Gemini_Generated_Image_khnsr6khnsr6khns.png'
    },
    {
      name: 'Julie A.',
      business: 'New Kitchen Restaurant',
      text: 'La gamification est brillante. Les clients adorent et notre taux de visite répétée a augmenté de 35% !',
      avatar: 'https://customer-assets.emergentagent.com/job_saas-enhance/artifacts/g1xbzga3_Gemini_Generated_Image_n75p7wn75p7wn75p.png'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 relative overflow-hidden">
      {/* Global Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/3 left-1/3 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>
      {/* Header */}
      <motion.header 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/10"
      >
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <Logo data-testid="logo" textClassName="text-2xl font-bold text-white" />
          </motion.div>
          
          <div className="flex gap-4">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')} 
                data-testid="login-button"
                className="text-white hover:text-blue-300 hover:bg-white/10 transition-all font-medium"
              >
                Se connecter
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative">
        <div className="container mx-auto text-center max-w-5xl relative z-10">
          {/* Rating Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block mb-6"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full blur-lg opacity-30"></div>
              <div className="relative inline-flex items-center gap-2 sm:gap-3 backdrop-blur-md bg-white/10 px-3 sm:px-6 py-2 sm:py-3 rounded-full border border-white/20 flex-wrap justify-center">
                <div className="flex -space-x-2">
                  {[
                    'https://customer-assets.emergentagent.com/job_saas-enhance/artifacts/9xwf8c63_Gemini_Generated_Image_9ao8qo9ao8qo9ao8__1_-removebg-preview.png',
                    'https://customer-assets.emergentagent.com/job_saas-enhance/artifacts/qafm6rj2_Gemini_Generated_Image_khnsr6khnsr6khns.png',
                    'https://customer-assets.emergentagent.com/job_saas-enhance/artifacts/g1xbzga3_Gemini_Generated_Image_n75p7wn75p7wn75p.png'
                  ].map((avatarUrl, i) => (
                    <img 
                      key={i} 
                      src={avatarUrl} 
                      alt={`Client ${i + 1}`}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border-2 border-white"
                    />
                  ))}
                </div>
                <div className="flex gap-0.5 sm:gap-1">
                  {[...Array(5)].map((_, i) => (
                    <motion.span
                      key={i}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.1 }}
                      className="text-yellow-400 text-base sm:text-lg"
                    >
                      ⭐
                    </motion.span>
                  ))}
                </div>
                <span className="text-white font-bold text-sm sm:text-base drop-shadow-lg">+100 clients satisfaits</span>
              </div>
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight" 
            data-testid="hero-title"
          >
            <motion.span 
              className="text-white"
              animate={{ 
                textShadow: [
                  "0 0 20px rgba(255,255,255,0.1)",
                  "0 0 30px rgba(255,255,255,0.2)",
                  "0 0 20px rgba(255,255,255,0.1)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Plus de clients, plus de visibilité.
            </motion.span>
            <br />
            <motion.span 
              className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent"
              animate={{ 
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
              }}
              transition={{ duration: 5, repeat: Infinity }}
              style={{ backgroundSize: "200% 200%" }}
            >
              Grâce à une IA qui travaille pour vous
            </motion.span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto" 
            data-testid="hero-subtitle"
          >
            Booste ton commerce sans exploser ton budget : l'IA de Markeap s'occupe de ta visibilité, de tes avis et de tes clients automatiquement.
          </motion.p>
          
          {/* Video Embed Placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            whileHover={{ scale: 1.02 }}
            className="mb-10 max-w-md mx-auto"
          >
            <div className="glass-card rounded-2xl overflow-hidden border-2 border-blue-500/30 hover:border-blue-500/50 transition-all relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative z-10" data-testid="video-container" style={{ aspectRatio: '9/16' }}>
                <video 
                  ref={videoRef}
                  className="w-full h-full object-cover rounded-xl"
                  autoPlay
                  muted
                  loop
                  playsInline
                  data-testid="explainer-video"
                >
                  <source src="https://customer-assets.emergentagent.com/job_fortune-wheel-fix/artifacts/2uogo73p_Markeap%20%281%29.mp4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
                {/* Bouton pour activer/désactiver le son */}
                <button
                  onClick={toggleMute}
                  className="absolute bottom-4 right-4 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all z-20"
                  data-testid="mute-toggle-btn"
                >
                  {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              size="lg" 
              onClick={handleStartCheckout}
              disabled={isCheckoutLoading}
              className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 hover:from-blue-700 hover:via-cyan-700 hover:to-blue-800 text-lg px-8 py-6 rounded-full shadow-2xl hover:shadow-blue-500/50 transition-all group disabled:opacity-70"
              data-testid="hero-cta-button"
            >
              <motion.div
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              />
              <span className="relative z-10 flex items-center gap-2">
                {isCheckoutLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Redirection...
                  </>
                ) : (
                  <>
                    Démarrer maintenant
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </span>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 relative" data-testid="features-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold text-center mb-4"
          >
            <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
              Tout ce dont Vous Avez Besoin pour Réussir
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center text-gray-400 text-lg mb-16"
          >
            Des outils puissants pour développer votre commerce
          </motion.p>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`feature-card-${index}`}
                className="group"
              >
                {/* Carte principale */}
                <motion.div
                  whileHover={{ y: -10 }}
                  className="glass-card rounded-2xl h-full hover-lift border border-white/10 overflow-hidden relative"
                >
                  {/* Image en pleine largeur */}
                  <div className="w-full overflow-hidden">
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                  {/* Contenu texte */}
                  <div className="relative z-10 p-6">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors">{feature.title}</h3>
                    <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors">{feature.description}</p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Redesigned */}
      <section className="py-20 px-6 relative" data-testid="how-it-works-section">
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold text-center mb-4"
          >
            <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
              Comment Ça Marche
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center text-gray-400 text-lg mb-16"
          >
            Un processus simple en 3 étapes pour booster votre commerce
          </motion.p>

          <div className="space-y-16">
            {/* Étape 1 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="step-1"
            >
              {/* Titre avec numéro */}
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg">
                  1
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  Vos clients scannent votre QR code
                </h3>
              </div>
              
              {/* Layout côte à côte sur desktop */}
              <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
                {/* Video Zone */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="w-full md:w-1/2"
                >
                  <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm">
                    <video 
                      className="w-full h-auto"
                      autoPlay
                      muted
                      loop
                      playsInline
                    >
                      <source src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/9qb665oe_etape1-clientsscanQrcode.mp4" type="video/mp4" />
                    </video>
                  </div>
                </motion.div>
                
                {/* Contenu texte */}
                <div className="w-full md:w-1/2">
                  <p className="text-gray-400 leading-relaxed mb-4 text-lg">
                    On vous envoie des supports personnalisés (table, vitrine, commandes à emporter…).
                  </p>
                  <p className="text-cyan-400 font-medium flex items-center gap-2 text-lg">
                    <span>➡️</span> Le QR code les envoie directement sur votre page de jeu.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Étape 2 */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="step-2"
            >
              {/* Titre avec numéro */}
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg">
                  2
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  Ils laissent un avis Google pour jouer
                </h3>
              </div>
              
              {/* Layout côte à côte sur desktop */}
              <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
                {/* Video Zone */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="w-full md:w-1/2"
                >
                  <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm">
                    <video 
                      className="w-full h-auto"
                      autoPlay
                      muted
                      loop
                      playsInline
                    >
                      <source src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/bmghk8t7_etape2-ils%20laissent%20un%20avis%20google.mp4" type="video/mp4" />
                    </video>
                  </div>
                </motion.div>
                
                {/* Contenu texte */}
                <div className="w-full md:w-1/2">
                  <p className="text-gray-400 leading-relaxed mb-4 text-lg">
                    Pour participer, le client :
                  </p>
                  <ul className="text-gray-400 space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-lg">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span>Laisse un avis Google sur votre fiche</span>
                    </li>
                    <li className="flex items-center gap-2 text-lg">
                      <Users className="w-5 h-5 text-cyan-500" />
                      <span>Renseigne nom + prénom + téléphone</span>
                    </li>
                  </ul>
                  <p className="text-cyan-400 font-medium flex items-center gap-2 text-lg">
                    <span>➡️</span> Et accède à la roue !
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Étape 3 */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="step-3"
            >
              {/* Titre avec numéro */}
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg">
                  3
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  Ils tournent la roue et récupèrent leur gain
                </h3>
              </div>
              
              {/* Layout côte à côte sur desktop */}
              <div className="flex flex-col md:flex-row gap-6 md:gap-10 items-start">
                {/* Video Zone */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="w-full md:w-1/2"
                >
                  <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm">
                    <video 
                      className="w-full h-auto"
                      autoPlay
                      muted
                      loop
                      playsInline
                    >
                      <source src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/7v78panl_etape3-tournent%20laroue.mp4" type="video/mp4" />
                    </video>
                  </div>
                </motion.div>
                
                {/* Contenu texte */}
                <div className="w-full md:w-1/2">
                  <p className="text-gray-400 leading-relaxed mb-4 text-lg">
                    Le client tourne la roue 🎁
                  </p>
                  <p className="text-cyan-400 font-medium mb-4 text-lg">
                    👀 Vous réglez vous-même les probabilités de gain.
                  </p>
                  <p className="text-gray-400 mb-2 text-lg">S'il gagne :</p>
                  <p className="text-cyan-400 font-medium flex items-center gap-2 text-lg">
                    <span>📩</span> Il reçoit un SMS avec un QR code, que vous scannez pour valider la récompense.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Markeap travaille pour vous (automatiquement) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mt-12"
            >
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-white mb-2">
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                    Et ensuite, Markeap s'occupe du reste
                  </span>
                </h3>
                <p className="text-gray-400">(Grâce à l'IA)</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Réponses automatiques aux avis Google */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm"
                >
                  <video 
                    className="w-full h-auto"
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/bacyktpj_reponses-automoatique-avosAVISCLIENTS.mp4" type="video/mp4" />
                  </video>
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-green-400 text-xl mt-1">✅</span>
                      <h4 className="text-xl font-bold text-white">
                        Réponses automatiques à vos avis Google (par IA)
                      </h4>
                    </div>
                    <p className="text-gray-400 leading-relaxed ml-8">
                      Dès qu'un avis tombe, Markeap répond automatiquement avec une réponse humaine + optimisée SEO local 
                      <span className="text-cyan-400"> (mots-clés de votre ville / quartier / activité)</span>.
                    </p>
                  </div>
                </motion.div>

                {/* Envoi de promos par SMS */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm"
                >
                  <video 
                    className="w-full h-auto"
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/81izjfzt_EnvoideromosparSMStousvosclients.mp4" type="video/mp4" />
                  </video>
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <span className="text-green-400 text-xl mt-1">✅</span>
                      <h4 className="text-xl font-bold text-white">
                        Envoi de promos par SMS à tous vos clients
                      </h4>
                    </div>
                    <p className="text-gray-400 leading-relaxed ml-8 mb-3">
                      Comme vous récupérez les numéros, vous pouvez relancer votre base en 1 clic :
                    </p>
                    <p className="text-cyan-400 ml-8 mb-3">
                      📲 promos, offres du moment, retours clients, événements…
                    </p>
                    <p className="text-gray-400 ml-8 flex items-start gap-2">
                      <span className="text-cyan-400">➡️</span>
                      <span>Beaucoup plus rentable que les flyers, et vous suivez les résultats.</span>
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ils nous font confiance Section */}
      <section className="py-20 px-6 relative" data-testid="trust-section">
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold text-center mb-4"
          >
            <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
              Ils nous font confiance
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center text-gray-400 text-lg mb-16"
          >
            et leurs avis Google le montrent.
          </motion.p>

          <div className="space-y-16">
            {/* Le Majestic */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="trust-majestic"
            >
              {/* Titre */}
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Le Majestic
              </h3>
              
              {/* Info ligne : Bouton + Type + Gérant + Date */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <a 
                  href="https://maps.app.goo.gl/8muPiiVMbVegxWwK8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white text-gray-900 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Vérifier sur Google
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </a>
                <span className="text-sm">
                  <span className="text-white font-semibold">Restaurant</span>
                  <span className="text-gray-400"> • Said A. • Mis à jour : Février 2026</span>
                </span>
              </div>
              
              {/* Image Zone - Pleine largeur */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="w-full mb-6"
              >
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/llhvsicf_lemajestic.png" 
                    alt="Le Majestic - Résultats Google"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
              
              {/* Description */}
              <div className="w-full">
                <p className="text-gray-400 leading-relaxed text-lg mb-4">
                  Le Majestic était à <span className="text-yellow-400 font-semibold">4,2 ⭐ avec 473 avis</span>, mais voulait passer un cap sur Google.
                </p>
                <p className="text-gray-400 leading-relaxed text-lg">
                  Avec Markeap, ils ont augmenté le volume d'avis de façon régulière et sont arrivés à <span className="font-semibold" style={{color: '#22D3EE'}}>4,8 ⭐ et +1 200 avis</span>, ce qui améliore fortement la visibilité locale et la confiance des nouveaux clients.
                </p>
              </div>
            </motion.div>

            {/* Barber Avenue Paris */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="trust-barber"
            >
              {/* Titre */}
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Barber Avenue Paris
              </h3>
              
              {/* Info ligne : Bouton + Type + Gérant + Date */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <a 
                  href="https://maps.app.goo.gl/iyQjDNXqmdUcH2t67" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white text-gray-900 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Vérifier sur Google
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </a>
                <span className="text-sm">
                  <span className="text-white font-semibold">Salon de coiffure</span>
                  <span className="text-gray-400"> • B. M. • Mis à jour : Février 2026</span>
                </span>
              </div>
              
              {/* Image Zone - Pleine largeur */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="w-full mb-6"
              >
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/acv2gj3s_barberavenueparis.png" 
                    alt="Barber Avenue Paris - Résultats Google"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
              
              {/* Description */}
              <div className="w-full">
                <p className="text-gray-400 leading-relaxed text-lg mb-4">
                  Barber Avenue est parti de zéro, autour de <span className="text-yellow-400 font-semibold">3,8 ⭐</span>, avec peu d'avis au départ.
                </p>
                <p className="text-gray-400 leading-relaxed text-lg">
                  Avec Markeap, ils ont structuré la récolte d'avis au quotidien et sont montés à <span className="font-semibold" style={{color: '#22D3EE'}}>4,8 ⭐ avec 945 avis</span>, ce qui a rendu leur fiche Google beaucoup plus rassurante et attractive.
                </p>
              </div>
            </motion.div>

            {/* New Kitchen Restaurant */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="trust-newkitchen"
            >
              {/* Titre */}
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                New Kitchen Restaurant
              </h3>
              
              {/* Info ligne : Bouton + Type + Gérant + Date */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <a 
                  href="https://maps.app.goo.gl/mN6XUKVHEx5oWSLA7" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white text-gray-900 px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Vérifier sur Google
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </a>
                <span className="text-sm">
                  <span className="text-white font-semibold">Restaurant</span>
                  <span className="text-gray-400"> • H.D • Mis à jour : Février 2026</span>
                </span>
              </div>
              
              {/* Image Zone - Pleine largeur */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="w-full mb-6"
              >
                <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_edabecc7-206e-45bd-88bb-191acb85dc29/artifacts/owfg01xy_newkitchenrestaurant.png" 
                    alt="New Kitchen Restaurant - Résultats Google"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
              
              {/* Description */}
              <div className="w-full">
                <p className="text-gray-400 leading-relaxed text-lg mb-4">
                  New Kitchen a commencé à utiliser Markeap fin 2025, avec une base d'avis encore limitée.
                </p>
                <p className="text-gray-400 leading-relaxed text-lg">
                  En quelques mois, la mécanique "scan → avis → roue" a accéléré la collecte et ils affichent aujourd'hui <span className="font-semibold" style={{color: '#22D3EE'}}>4,8 ⭐ avec près de 500 avis</span>, tout en gardant une excellente note.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Single Offer */}
      <section className="py-20 px-6 relative" data-testid="pricing-section">
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.05, 1],
                rotate: [0, 2, -2, 0]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="inline-block relative mb-6"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-pink-500 rounded-full blur-xl opacity-60 animate-pulse"></div>
              <div className="relative bg-gradient-to-r from-red-500 to-pink-600 text-white px-8 py-3 rounded-full font-bold text-sm shadow-2xl">
                🔥 OFFRE LIMITÉE
              </div>
            </motion.div>
            
            <motion.h2 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
            >
              <span className="text-white">Obtenez </span>
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent animate-gradient">
                Tout
              </span>
              <span className="text-white">. Maintenant.</span>
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-xl text-gray-300"
            >
              La seule solution dont vous avez besoin pour exploser votre commerce
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.01 }}
            className="glass-card rounded-3xl overflow-hidden shadow-2xl hover-lift border-2 border-blue-500/30"
            data-testid="single-pricing-card"
          >
            {/* Price Section */}
            <div className="relative bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 text-white p-12 text-center overflow-hidden">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>
              
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative z-10"
              >
                <div className="flex items-center justify-center gap-6 mb-4">
                  <motion.span 
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="text-4xl line-through opacity-60"
                  >
                    169€
                  </motion.span>
                  
                  <motion.div
                    animate={{ 
                      textShadow: [
                        "0 0 20px rgba(255,255,255,0.5)",
                        "0 0 40px rgba(255,255,255,0.8)",
                        "0 0 20px rgba(255,255,255,0.5)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-7xl sm:text-8xl font-black"
                  >
                    89€
                  </motion.div>
                </div>
                
                <p className="text-2xl mb-3 font-semibold">par mois</p>
                
                <motion.div
                  animate={{ opacity: [0.8, 1, 0.8] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full"
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-medium">Économisez 80€/mois • Soit 960€/an</span>
                </motion.div>
              </motion.div>
            </div>

            {/* Features Section */}
            <div className="p-8 md:p-12">
              <div className="mb-10">
                <motion.h3 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-3xl font-bold text-white mb-8 flex items-center gap-3"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Check className="w-8 h-8 text-green-400" />
                  </motion.div>
                  <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
                    Inclus dans votre abonnement :
                  </span>
                </motion.h3>
                
                <div className="space-y-4">
                  {offerFeatures.included.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ 
                        x: 10,
                        backgroundColor: "rgba(59, 130, 246, 0.05)"
                      }}
                      className="flex gap-4 p-4 rounded-xl transition-all duration-300 cursor-pointer group"
                    >
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        transition={{ duration: 0.3 }}
                        className="bg-gradient-to-br from-blue-100 to-cyan-100 p-3 rounded-xl flex-shrink-0 group-hover:from-blue-200 group-hover:to-cyan-200 transition-all"
                      >
                        <div className="text-blue-600 group-hover:text-blue-700 transition-colors">
                          {feature.icon}
                        </div>
                      </motion.div>
                      <div>
                        <p className="font-bold text-white text-lg mb-1 group-hover:text-blue-300 transition-colors">{feature.title}</p>
                        <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Bonuses */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border-2 border-yellow-500/40 rounded-2xl p-8 mb-8 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500 rounded-full filter blur-3xl opacity-20"></div>
                
                <motion.h3 
                  className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center gap-3 relative z-10"
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <motion.span
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="text-3xl"
                  >
                    🎁
                  </motion.span>
                  En plus, vous obtenez :
                </motion.h3>
                
                <div className="space-y-4 relative z-10">
                  {offerFeatures.bonuses.map((bonus, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ x: 5 }}
                      className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-4 bg-blue-900/30 backdrop-blur-sm p-3 sm:p-4 rounded-xl hover:bg-blue-800/40 transition-all duration-300 group border border-blue-500/20"
                    >
                      <div className="flex items-center gap-3">
                        <motion.div
                          whileHover={{ scale: 1.3, rotate: 360 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Check className="w-5 h-5 text-green-400 flex-shrink-0 group-hover:text-green-300 transition-colors" />
                        </motion.div>
                        <span className="font-semibold text-white text-sm sm:text-base">{bonus.text}</span>
                      </div>
                      <motion.span 
                        whileHover={{ scale: 1.1 }}
                        className="text-green-300 font-bold bg-green-900/50 px-3 py-1 rounded-full text-xs sm:text-sm border border-green-500/30 self-start sm:self-auto ml-8 sm:ml-0"
                      >
                        {bonus.value}
                      </motion.span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Value Stack */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative bg-gradient-to-br from-blue-900/40 to-cyan-900/40 rounded-2xl p-4 sm:p-8 mb-8 overflow-hidden border-2 border-blue-500/30"
              >
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImRvdHMiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9IiMzYjgyZjYiIG9wYWNpdHk9IjAuMiIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNkb3RzKSIvPjwvc3ZnPg==')] opacity-50"></div>
                
                <div className="relative z-10">
                  <motion.div 
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    viewport={{ once: true }}
                    className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 text-base sm:text-lg mb-3"
                  >
                    <span className="font-semibold text-gray-300">Valeur totale :</span>
                    <span className="line-through text-gray-400 text-lg sm:text-xl">368€/mois</span>
                  </motion.div>
                  
                  <motion.div 
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    viewport={{ once: true }}
                    className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0"
                  >
                    <span className="text-lg sm:text-2xl font-bold text-white">Vous payez seulement :</span>
                    <motion.span 
                      animate={{ 
                        scale: [1, 1.05, 1],
                        textShadow: [
                          "0 0 10px rgba(74, 222, 128, 0.5)",
                          "0 0 20px rgba(74, 222, 128, 0.7)",
                          "0 0 10px rgba(74, 222, 128, 0.5)"
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="text-3xl sm:text-4xl font-black text-green-400"
                    >
                      89€/mois
                    </motion.span>
                  </motion.div>
                </div>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button 
                    size="lg"
                    onClick={handleStartCheckout}
                    disabled={isCheckoutLoading}
                    className="w-full relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 hover:from-blue-700 hover:via-cyan-700 hover:to-blue-800 text-xl py-8 shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 group disabled:opacity-70"
                    data-testid="claim-offer-button"
                  >
                    <motion.div
                      animate={{ x: ["-100%", "100%"] }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    />
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      {isCheckoutLoading ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" />
                          Redirection...
                        </>
                      ) : (
                        <>
                          Démarrer maintenant
                          <ArrowRight className="w-6 h-6" />
                        </>
                      )}
                    </span>
                  </Button>
                </motion.div>
                
                <motion.p 
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-center text-white text-sm mt-4 font-medium"
                >
                  ⚡ Plus que quelques places disponibles à ce prix
                </motion.p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 text-white relative" data-testid="testimonials-section">
        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold text-center mb-4"
          >
            <span className="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">
              Adoré par les Petits Commerces
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-center text-gray-400 text-lg mb-16"
          >
            Découvrez ce que nos clients disent de nous
          </motion.p>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                data-testid={`testimonial-${index}`}
              >
                <div className="glass-card p-8 rounded-2xl h-full hover-lift border-2 border-blue-500/20 hover:border-blue-500/40 transition-all group relative overflow-hidden">
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/10 to-transparent shimmer"></div>
                  
                  <div className="relative z-10">
                    <div className="flex gap-1 mb-6">
                      {[...Array(5)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ scale: 0, rotate: -180 }}
                          whileInView={{ scale: 1, rotate: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.15 + i * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        </motion.div>
                      ))}
                    </div>
                    <p className="text-gray-300 mb-6 italic leading-relaxed group-hover:text-white transition-colors">"{testimonial.text}"</p>
                    <div className="pt-4 border-t border-white/10 flex items-center gap-4">
                      <img 
                        src={testimonial.avatar} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-blue-400 flex-shrink-0"
                      />
                      <div>
                        <p className="font-bold text-white group-hover:text-blue-300 transition-colors">{testimonial.name}</p>
                        <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">{testimonial.business}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 text-white relative" data-testid="final-cta-section">
        <div className="container mx-auto text-center max-w-4xl relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.h2 
              animate={{ 
                textShadow: [
                  "0 0 20px rgba(255,255,255,0.3)",
                  "0 0 40px rgba(255,255,255,0.5)",
                  "0 0 20px rgba(255,255,255,0.3)"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
            >
              Prêt à Transformer Votre Commerce ?
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-xl mb-10 text-blue-100"
            >
              Rejoignez des centaines de commerces qui se développent déjà avec Markeap
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                size="lg" 
                onClick={handleStartCheckout}
                disabled={isCheckoutLoading}
                className="relative overflow-hidden bg-white text-blue-600 hover:bg-gray-50 text-xl px-10 py-8 rounded-full shadow-2xl hover:shadow-white/20 transition-all group font-bold disabled:opacity-70"
                data-testid="final-cta-button"
              >
                <motion.div
                  animate={{ x: ["-100%", "100%"] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-200/30 to-transparent"
                />
                <span className="relative z-10 flex items-center gap-3">
                  {isCheckoutLoading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      Redirection...
                    </>
                  ) : (
                    <>
                      Commencer Maintenant
                      <ArrowRight className="w-6 h-6" />
                    </>
                  )}
                </span>
              </Button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="text-blue-100 text-sm mt-6"
            >
              ✨ Sans engagement • Démarrage immédiat
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950/50 backdrop-blur-sm border-t border-white/5 text-gray-400 py-12 px-6 relative">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <Logo textClassName="text-xl font-bold text-white" className="w-6 h-6" />
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-sm text-center sm:text-left">
              <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">
                Politique de Confidentialité
              </button>
              <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">
                Conditions d'Utilisation
              </button>
              <button onClick={() => navigate('/refund')} className="hover:text-white transition-colors">
                Politique de Remboursement
              </button>
              <button onClick={() => navigate('/contact')} className="hover:text-white transition-colors">
                Contact
              </button>
            </div>
            <div className="flex gap-4">
              <Users className="w-5 h-5 hover:text-white cursor-pointer transition-colors" />
              <TrendingUp className="w-5 h-5 hover:text-white cursor-pointer transition-colors" />
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-sm">© 2025 Markeap. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
